import { useState } from 'react';
import { Group, Text, Menu, Avatar, UnstyledButton, Box, ActionIcon, useMantineTheme, Stack, Loader } from '@mantine/core';
import { IconLogout, IconUser, IconPlus, IconSparkles, IconCards, IconBook, IconClipboardList, IconPlayerPlay, IconMicrophone } from '@tabler/icons-react';
import { useStudentProfile } from '@/features/profile/hooks/useProfile';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useLogout } from '@/features/auth/hooks/useAuth';
import { AssignmentModal } from '@/features/parent/components/AssignmentModal';
import { parentApi } from '@/features/parent/api/parentApi';
import { useQuickRead, useParentQuickRead } from '@/features/recommendations';
import { useRecommendAssignments } from '@/features/parent/hooks/useParent';
import { APP_NAME } from '@/config/constants';
import { capitalizeName } from '@/utils/name';

interface ChildQuickReadItemProps {
  childId: string;
  childName: string;
  onClose: () => void;
}

function ChildQuickReadItem({ childId, childName, onClose }: ChildQuickReadItemProps) {
  const theme = useMantineTheme();
  const { startQuickRead, isDisabled } = useParentQuickRead({
    studentId: childId,
    childName,
  });

  return (
    <Menu.Item
      leftSection={<IconPlayerPlay size={16} stroke={1.5} />}
      onClick={() => {
        startQuickRead();
        onClose();
      }}
      disabled={isDisabled}
      color="teal"
      styles={{
        item: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: theme.colors.teal[1],
            color: theme.colors.teal[8],
            transform: 'translateX(2px)',
          },
        },
      }}
    >
      Read with {childName}
    </Menu.Item>
  );
}

export function Header() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const handleLogout = useLogout();
  const { data: profile } = useStudentProfile();
  const theme = useMantineTheme();
  const [createMenuOpened, setCreateMenuOpened] = useState(false);
  const [readingAssignmentOpened, setReadingAssignmentOpened] = useState(false);
  const [flashcardAssignmentOpened, setFlashcardAssignmentOpened] = useState(false);
  const recommendAssignments = useRecommendAssignments();

  const isParent = user?.roles?.includes('parent');
  const isStudent = user?.roles?.includes('student');

  // Student QuickRead
  const { startQuickRead: startStudentQuickRead, isDisabled: studentQuickReadDisabled } = useQuickRead();

  // Fetch children for QuickRead (parents only)
  const { data: children, isLoading: childrenLoading } = useQuery({
    queryKey: ['children', user?.id],
    queryFn: () => parentApi.getChildren(),
    enabled: !!user?.id && isParent,
  });

  // Get user's display name and first letter for avatar
  const getUserDisplayName = () => {
    const name = user?.firstName || user?.profile?.firstName || user?.profile?.preferredName || user?.username || '';
    return capitalizeName(name);
  };

  const getUserInitial = () => {
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase() || 'U';
  };

  // Shared avatar styles — matches the "+" Create button (teal[2] bg, teal[9] glyph)
  const getAvatarStyles = (size: number) => ({
    root: {
      backgroundColor: theme.colors.teal[2],
      fontSize: size === 48 ? '24px' : '18px',
      fontWeight: 700,
    },
    placeholder: {
      backgroundColor: theme.colors.teal[2],
      color: theme.colors.teal[9],
      fontWeight: 700,
    },
  });

  return (
    <Group h="100%" px="md" py="sm" justify="space-between" wrap="nowrap">
      {/* Logo/Brand */}
      <Group
        gap="xs"
        wrap="nowrap"
        styles={{
          root: {
            cursor: 'pointer',
          },
        }}
        onClick={() => navigate(user?.roles?.includes('parent') ? '/parent' : '/')}
      >
        <Text size="28px" fw={700} styles={{ root: { whiteSpace: 'nowrap' } }}>
          {APP_NAME}
        </Text>
      </Group>

      {/* User Menu */}
      <Group gap="sm" wrap="nowrap">
        {/* Create Button */}
        <Menu
          opened={createMenuOpened}
          onChange={setCreateMenuOpened}
          position="bottom-end"
          shadow="md"
          width={200}
        >
          <Menu.Target>
            <ActionIcon
              size={48}
              radius="xl"
              styles={{
                root: {
                  backgroundColor: theme.colors.teal[2],
                  color: theme.colors.teal[9],
                  transition: 'transform 0.2s ease, background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.colors.teal[3],
                    transform: 'scale(1.05)',
                  },
                },
              }}
            >
              <IconPlus
                size={24}
                stroke={2.5}
                style={{
                  transition: 'transform 0.2s ease',
                  transform: createMenuOpened ? 'rotate(45deg)' : 'rotate(0deg)',
                }}
              />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            {/* Student items */}
            {isStudent && (
              <>
                <Menu.Item
                  leftSection={<IconPlayerPlay size={16} stroke={1.5} />}
                  onClick={() => {
                    startStudentQuickRead();
                    setCreateMenuOpened(false);
                  }}
                  disabled={studentQuickReadDisabled}
                  color="teal"
                  styles={{
                    item: {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.colors.teal[1],
                        color: theme.colors.teal[8],
                        transform: 'translateX(2px)',
                      },
                    },
                  }}
                >
                  Quick Read
                </Menu.Item>
                <Menu.Divider />
                <Menu.Label>Create New</Menu.Label>
                <Menu.Item
                  leftSection={<IconSparkles size={16} stroke={1.5} />}
                  onClick={() => {
                    navigate('/library/story/create');
                    setCreateMenuOpened(false);
                  }}
                  styles={{
                    item: {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.colors.teal[1],
                        color: theme.colors.teal[8],
                        transform: 'translateX(2px)',
                      },
                    },
                  }}
                >
                  Story
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconCards size={16} stroke={1.5} />}
                  onClick={() => {
                    navigate('/flashcards/create');
                    setCreateMenuOpened(false);
                  }}
                  styles={{
                    item: {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.colors.teal[1],
                        color: theme.colors.teal[8],
                        transform: 'translateX(2px)',
                      },
                    },
                  }}
                >
                  Flashcard Deck
                </Menu.Item>
              </>
            )}

            {/* Parent items */}
            {isParent && (
              <>
                {/* Quick Read section */}
                <Menu.Label>Quick Read</Menu.Label>
                {childrenLoading ? (
                  <Menu.Item disabled leftSection={<Loader size={16} />}>
                    Loading...
                  </Menu.Item>
                ) : children && children.length > 0 ? (
                  children.map((child) => (
                    <ChildQuickReadItem
                      key={child.id}
                      childId={child.id}
                      childName={capitalizeName(child.name)}
                      onClose={() => setCreateMenuOpened(false)}
                    />
                  ))
                ) : (
                  <Menu.Item disabled>No children added</Menu.Item>
                )}

                {/* Read Aloud — only when at least one child has fluency enabled */}
                {children && children.some((c: any) => c.fluency_enabled) && (
                  <>
                    <Menu.Divider />
                    <Menu.Label>Read Aloud</Menu.Label>
                    {children
                      .filter((c: any) => c.fluency_enabled)
                      .map((child: any) => (
                        <Menu.Item
                          key={`fluency-${child.id}`}
                          leftSection={<IconMicrophone size={16} stroke={1.5} />}
                          onClick={() => {
                            navigate(`/parent/fluency/start?studentId=${child.id}`);
                            setCreateMenuOpened(false);
                          }}
                          styles={{
                            item: {
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: theme.colors.teal[1],
                                color: theme.colors.teal[8],
                                transform: 'translateX(2px)',
                              },
                            },
                          }}
                        >
                          {capitalizeName(child.name)}
                        </Menu.Item>
                      ))}
                  </>
                )}

                {/* Music — only when at least one child has music enabled */}
                {children && children.some((c: any) => c.music_enabled) && (
                  <>
                    <Menu.Divider />
                    <Menu.Label>Music</Menu.Label>
                    {children
                      .filter((c: any) => c.music_enabled)
                      .map((child: any) => (
                        <Menu.Item
                          key={`music-${child.id}`}
                          onClick={() => {
                            navigate(`/music?studentId=${child.id}`);
                            setCreateMenuOpened(false);
                          }}
                          styles={{
                            item: {
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: theme.colors.teal[1],
                                color: theme.colors.teal[8],
                                transform: 'translateX(2px)',
                              },
                            },
                          }}
                        >
                          {capitalizeName(child.name)}
                        </Menu.Item>
                      ))}
                  </>
                )}
                <Menu.Divider />

                <Menu.Label>Create New</Menu.Label>
                <Menu.Item
                  leftSection={<IconSparkles size={16} stroke={1.5} />}
                  onClick={() => {
                    navigate('/library/story/create');
                    setCreateMenuOpened(false);
                  }}
                  styles={{
                    item: {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.colors.teal[1],
                        color: theme.colors.teal[8],
                        transform: 'translateX(2px)',
                      },
                    },
                  }}
                >
                  Story
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconCards size={16} stroke={1.5} />}
                  onClick={() => {
                    navigate('/flashcards/create');
                    setCreateMenuOpened(false);
                  }}
                  styles={{
                    item: {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.colors.teal[1],
                        color: theme.colors.teal[8],
                        transform: 'translateX(2px)',
                      },
                    },
                  }}
                >
                  Flashcard Deck
                </Menu.Item>
                <Menu.Divider />
                {/* Smart Pick - per child */}
                <Menu.Label>Smart Pick</Menu.Label>
                {children && children.length > 0 ? (
                  children.map((child) => (
                    <Menu.Item
                      key={`smart-${child.id}`}
                      leftSection={<IconSparkles size={16} stroke={1.5} />}
                      onClick={() => {
                        recommendAssignments.mutate(child.id);
                        setCreateMenuOpened(false);
                      }}
                      disabled={recommendAssignments.isPending}
                      styles={{
                        item: {
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: theme.colors.teal[1],
                            color: theme.colors.teal[8],
                            transform: 'translateX(2px)',
                          },
                        },
                      }}
                    >
                      Assign for {capitalizeName(child.name)}
                    </Menu.Item>
                  ))
                ) : (
                  <Menu.Item disabled>No children added</Menu.Item>
                )}
                <Menu.Divider />
                <Menu.Label>New Assignment</Menu.Label>
                <Menu.Item
                  leftSection={<IconBook size={16} stroke={1.5} />}
                  onClick={() => {
                    setReadingAssignmentOpened(true);
                    setCreateMenuOpened(false);
                  }}
                  styles={{
                    item: {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.colors.teal[1],
                        color: theme.colors.teal[8],
                        transform: 'translateX(2px)',
                      },
                    },
                  }}
                >
                  Reading
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconClipboardList size={16} stroke={1.5} />}
                  onClick={() => {
                    setFlashcardAssignmentOpened(true);
                    setCreateMenuOpened(false);
                  }}
                  styles={{
                    item: {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.colors.teal[1],
                        color: theme.colors.teal[8],
                        transform: 'translateX(2px)',
                      },
                    },
                  }}
                >
                  Flashcard
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>

        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <UnstyledButton>
              <Avatar
                size={48}
                radius="xl"
                src={user?.picture || undefined}
                styles={getAvatarStyles(48)}
              >
                {getUserInitial()}
              </Avatar>
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            <Box
              p="sm"
              style={{ borderBottom: `1px solid ${theme.colors.gray[2]}` }}
            >
              <Group gap="sm" wrap="nowrap">
                <Avatar
                  size={48}
                  radius="xl"
                  src={user?.picture || undefined}
                  styles={getAvatarStyles(48)}
                >
                  {getUserInitial()}
                </Avatar>
                <Stack gap={0} style={{ flex: 1 }}>
                  <Text size="md" fw={600}>
                    {getUserDisplayName()}
                  </Text>
                  <Text size="xs" c="dimmed" tt="capitalize">
                    {user?.roles?.join(', ')}
                  </Text>
                </Stack>
              </Group>
            </Box>
            <Menu.Item
              leftSection={<IconUser size={16} stroke={1.5} />}
              onClick={() => navigate('/profile')}
              styles={{
                item: {
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.colors.teal[1],
                    color: theme.colors.teal[8],
                    transform: 'translateX(2px)',
                  },
                },
              }}
            >
              Profile
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              color="red"
              leftSection={<IconLogout size={16} stroke={1.5} />}
              onClick={handleLogout}
              styles={{
                item: {
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.colors.red[6],
                    color: 'white',
                    transform: 'translateX(2px)',
                  },
                },
              }}
            >
              Logout
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Assignment Modals */}
      {isParent && (
        <>
          <AssignmentModal
            opened={readingAssignmentOpened}
            onClose={() => setReadingAssignmentOpened(false)}
            type="story_theme"
          />
          <AssignmentModal
            opened={flashcardAssignmentOpened}
            onClose={() => setFlashcardAssignmentOpened(false)}
            type="flashcard_deck"
          />
        </>
      )}
    </Group>
  );
}
